import React, { useEffect, useState } from 'react';
import { array, bool, func, object } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import classNames from 'classnames';

import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import { propTypes } from '../../util/types';

import { H3, PrimaryButton, IconChecklist } from '../../components';

import { getAvailablePlans } from '../../ducks/stripe.duck';

import css from './SubscriptionPlans.module.css';

import verified from './../../assets/verified.png';
import recommended from './../../assets/recommended.png';
import { pathByRouteName } from '../../util/routes';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';

let numeral = require('numeral');

const SubscriptionPlansComponent = props => {
  const history = useHistory();
  const routeConfiguration = useRouteConfiguration();
  const [checked, setChecked] = useState(true);
  const [planPeriodicity, setPlanPeriodicity] = useState('month'); // month | year
  const [hoveredPlanId, setHoveredPlanId] = useState(null);

  const {
    availablePlans: plansRaw = [],
    plansLoaded = false,
    intl,
    isRedirectMode = true,
    isModalMode = true,
    onFetchAvailablePlans,
    onSelectPlan,
    onSetPeriodicity,
    allowToPurchase = false,
    hideTitle = false,
  } = props;

  useEffect(() => {
    if (!plansLoaded) {
      onFetchAvailablePlans();
    }
  }, [plansLoaded, onFetchAvailablePlans]);

  const handleChange = () => {
    let selectedPeriodicity = !checked ? 'month' : 'year'
    setChecked(!checked);
    setPlanPeriodicity(selectedPeriodicity);
    if (onSetPeriodicity) {
      onSetPeriodicity(selectedPeriodicity);
    }
  };

  const handlePeriodicity = (periodicity, disabled = false) => {
    if (!disabled) {
      if (planPeriodicity !== periodicity) {
        setChecked(!checked);
      }
      setPlanPeriodicity(periodicity);
      if (onSetPeriodicity) {
        onSetPeriodicity(periodicity);
      }
    }
  };

  const handleSelectPlan = (plan, force = false) => {
    if (isRedirectMode && !isModalMode) {
      let route = pathByRouteName('SubscriptionPage', routeConfiguration);
      history.push(route);
    } else {
      if (isModalMode) {
        onSelectPlan(plan.id, planPeriodicity);
      } else {
        onSelectPlan(plan.id, force);
      }
    }
  };

  const hasPlans = plansRaw && Array.isArray(plansRaw) && plansRaw.length;
  let plans = [];

  if (hasPlans) {
    plans = plansRaw.map(plan => {
      const { is_free = 'false' } = plan?.metadata || {};
      const isFree = is_free?.toLowerCase() === 'true';
      plan.isFree = isFree;

      let currentPrice = plan.prices.find(price => price.recurring.interval === planPeriodicity);
      let hasPrice = !!currentPrice;
      let price = hasPrice ? currentPrice.unit_amount / 100 : 0;
      price = numeral(price).format('0,0');

      plan.hasPrice = hasPrice;
      plan.printedPrice = price;

      const hasPeriodicity = currentPrice?.type === 'recurring';
      const periodicityLabel = hasPeriodicity ? currentPrice?.recurring?.interval : null;

      plan.rate = currentPrice?.rate || null;
      plan.hasPeriodicity = hasPeriodicity;
      plan.periodicityLabel = periodicityLabel;
      plan.planFeatures = (plan.features || plan.marketing_features || []).map(feature => feature.name);

      return plan;
    }).sort((planA, planB) => {
      if (planA.isFree && !planB.isFree) return -1;
      if (!planA.isFree && planB.isFree) return 1;

      const monthlyPriceA = planA.prices.find(price => price.recurring.interval === 'month');
      const annualPriceA = planA.prices.find(price => price.recurring.interval === 'year');
      const monthlyPriceB = planB.prices.find(price => price.recurring.interval === 'month');
      const annualPriceB = planB.prices.find(price => price.recurring.interval === 'year');

      const lowestPriceA = Math.min(
        monthlyPriceA ? monthlyPriceA.unit_amount : Infinity,
        annualPriceA ? annualPriceA.unit_amount : Infinity
      );
      const lowestPriceB = Math.min(
        monthlyPriceB ? monthlyPriceB.unit_amount : Infinity,
        annualPriceB ? annualPriceB.unit_amount : Infinity
      );

      return lowestPriceA - lowestPriceB;
    });
  }

  const periodicitySelector = (
    <div className={css.periodicityContainer}>
      <div role="button" className={css.periodicityLabelSelector} onClick={() => handlePeriodicity('month', false)}>
        {intl.formatMessage({ id: 'Pricing.monthly' })}
      </div>
      <div className={css.periodicityToggleModule}>
        <label className={css.periodicitySwitch}>
          <input type="checkbox" checked={checked} onChange={handleChange} disabled={false} />
          <span className={checked ? css.periodicitySlider : css.checkedPeriodicitySlider}></span>
        </label>
      </div>
      <div role="button" className={css.periodicityLabelSelector} onClick={() => handlePeriodicity('year', false)}>
        {intl.formatMessage({ id: 'Pricing.annually' })}
      </div>
    </div>
  );

  const availablePlansItems = (
    <>
      {hasPlans && periodicitySelector}
      <div className={css.plansFullContainer}>
        {plans.map((plan, key) => {
          const priceData = plan.prices.find(price => price.recurring.interval === planPeriodicity);

          if (!priceData) return null;

          const formattedPrice = (priceData.unit_amount / 100).toFixed(0);
          const currency = priceData.currency.toUpperCase();

          const {
            is_popular = false,
            is_verified = false,
            is_recommended = false,
          } = Object.fromEntries(
            Object.entries(plan?.metadata || {}).map(([k, v]) => [k, v === 'true'])
          );

          let notPurchaseAction = isModalMode || isRedirectMode;
          let buttonProps = notPurchaseAction ? { type: 'button' } : {};

          const isHovered = hoveredPlanId === plan.id;
          const planContainerClasses = classNames(css.pricingPlanContainer, { [css.hoveredPlan]: isHovered });

          return (
            <div
              className={planContainerClasses}
              key={key}
              onMouseEnter={() => setHoveredPlanId(plan.id)}
              onMouseLeave={() => setHoveredPlanId(null)}
            >
              {is_popular ? <div className={css.popularLabel}>{intl.formatMessage({ id: `Pricing.isPopular` })}</div> : null}
              {is_recommended ?
                <img src={recommended} alt="recommended" className={css.planAsset} />
                // : is_verified ? <img src={verified} alt="verified" className={css.planAsset} />
                : null
              }
              <div className={css.header}>
                <div className={css.price}>
                  ${formattedPrice} <span className={css.currency}>{currency}</span><span className={css.periodicityLabel}> /{plan.periodicityLabel}</span>
                </div>
              </div>

              <div className={css.priceContent}>
                <div className={css.selectionTitle}>
                  {plan.name}
                </div>
                <p className={css.description}>
                  {plan.description}
                </p>

                {isHovered && (<ul className={css.featureList}>
                  {plan.planFeatures.map((feature, index) => (
                    <li key={index} className={css.featureItem}>
                      <span className={css.checkmark}><IconChecklist /></span> {feature}
                    </li>
                  ))}
                </ul>)}
              </div>

              {allowToPurchase || plan.isFree || notPurchaseAction ? <div className={css.footer}>
                <PrimaryButton
                  className={css.chooseButton}
                  onClick={() => handleSelectPlan(plan, true)}
                  {...buttonProps}
                >
                  {intl.formatMessage({ id: 'Pricing.choosePlan' })}
                </PrimaryButton>
              </div> : null}
            </div>
          );
        })}
      </div>
    </>
  );

  return (
    <div className={css.content}>
      {!hideTitle ? (!isRedirectMode ? <H3 as="h1" className={css.title}>
        <FormattedMessage id="PaymentMethodsPage.subscribeHeading" />
      </H3> : <div className={css.pageTitleContainer}>
        <div className={css.pageTitle}>{intl.formatMessage({ id: 'PricingPage.title' })}</div>
        <div className={css.pageSubtitle}>{intl.formatMessage({ id: 'PricingPage.subtitle' })}</div>
      </div>) : null}
      <div className={classNames(css.optionsContainer, {[css.plansContainer]: plansRaw && plansRaw.length, [css.fullContainer]: isRedirectMode })}>
        {plansLoaded ? (plansRaw.length ? availablePlansItems : <p><FormattedMessage id="PaymentMethodsPage.noAvailablePlans" /></p>) : <p className={css.plansLoader}><FormattedMessage id="PaymentMethodsPage.loadingPlans" /></p>}
      </div>
    </div>
  );
};

SubscriptionPlansComponent.propTypes = {
  availablePlans: array,
  plansLoaded: bool,
  isRedirectMode: bool,
  onFetchAvailablePlans: func.isRequired,
  intl: intlShape.isRequired,
};

const mapStateToProps = state => {
  const {
    availablePlans,
    plansLoaded,
  } = state.stripe;

  return {
    availablePlans,
    plansLoaded,
  };
};

const mapDispatchToProps = dispatch => ({
  onFetchAvailablePlans: () => dispatch(getAvailablePlans()),
});

const SubscriptionPlans = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  ),
  injectIntl
)(SubscriptionPlansComponent);

export default SubscriptionPlans;
